import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import MenuButton, { IMenuItem } from '../model/MenuButton';
import FunctionalityDetailBody from '../model/functionality/FunctionalityDetailBody';
import ProtocolDetailBody from '../model/protocol/ProtocolDetailBody';
import DeviceModelDetailBody from '../model/deviceModel/DeviceModelDetailBody';
import { IconButton, useAlert } from '@destination/components';

import { IModel } from '@/models/IModelBasic';
import { Type } from '@/models/Type';
import { fullArtifactUrl } from '@/api/paths';
import { capitalizeFirstLetter } from '@/utils/text-utils';

import DropdownIcon from '@icons/down_16.svg?react';

export interface IGridModelRow {
  model: IModel;
  defaultOpen?: boolean;
  showOlderVersionsOption?: boolean;
}

export default function GridModelRow({
  model,
  defaultOpen,
  showOlderVersionsOption
}: IGridModelRow) {
  const [isExpanded, setIsExpanded] = useState(Boolean(defaultOpen));
  const navigate = useNavigate();

  const { t } = useTranslation(undefined, { keyPrefix: 'browse' });

  const { notify } = useAlert();

  const modelFields = [
    capitalizeFirstLetter(model.type),
    model.module,
    model.key,
    model.releases[0]?.version,
    capitalizeFirstLetter(model.status)
  ];

  const copyToClipboard = (message: string) => {
    navigator.clipboard.writeText(message);
    notify({ variant: 'message', header: t('options_button.notify_copy') });
  };

  const options: IMenuItem[] = [
    { label: 'json', onClick: () => navigate(`/models/${model.aggregateId}`) }
  ];

  if (model.releases[0]?.artifacts.length > 0) {
    const { version, artifacts } = model.releases[0];

    const artifactUrl = fullArtifactUrl(model);
    const message = `implementation group: 'com.enervalis.${model.type}.${model.module}', name: '${model.key}', version: '${version}'`;

    const artifactOptions: IMenuItem[] = [
      { label: 'artifact', onClick: () => window.open(artifactUrl, '_blank') },
      { label: 'gradle', onClick: () => copyToClipboard(message) },
      ...artifacts.map(artifact => ({
        label: artifact.type.toLowerCase(),
        onClick: () => window.open(artifact.location, '_blank')
      }))
    ];

    options.push(...artifactOptions);
  }

  if (showOlderVersionsOption) {
    options.push({
      label: 'browse_versions',
      onClick: () =>
        navigate(
          `/models/browse?type=${model.type}&module=${model.module}&key=${model.key}&includeOlderVersions=true&strict=true`
        )
    });
  }

  const renderDetailBody = () => {
    if (model.type == Type.FUNCTIONALITY) {
      return (
        <FunctionalityDetailBody model={model} defaultOpen={defaultOpen} />
      );
    }

    if (model.type == Type.PROTOCOL) {
      return <ProtocolDetailBody model={model} />;
    }

    if (model.type == Type.DEVICE_MODEL) {
      return <DeviceModelDetailBody model={model} />;
    }

    return <div>No detail body available for this model type</div>;
  };

  return (
    <>
      {modelFields.map((modelField, idx) => (
        <div
          className="text-base whitespace-normal cursor-pointer pl-3 pr-3 min-h-[34px] flex items-center my-1 center"
          key={`field${idx}-${model.aggregateId}`}
          data-test-id="grid-model-row"
          onClick={() =>
            setIsExpanded(previouslyExpanded => !previouslyExpanded)
          }
        >
          {idx == 0 ? modelField.replace('_', ' ') : modelField}
        </div>
      ))}
      <div className="flex justify-center">
        <IconButton
          data-testid="dropdown-btn"
          className={isExpanded ? 'rotate-180' : ''}
          onClick={() =>
            setIsExpanded(previouslyExpanded => !previouslyExpanded)
          }
        >
          <DropdownIcon />
        </IconButton>
      </div>
      <div className="flex justify-center items-center">
        <MenuButton options={options} />
      </div>
      {isExpanded && (
        <div style={{ gridColumn: '1 / -1' }} className="space-y-2 mb-4">
          {renderDetailBody()}
        </div>
      )}
    </>
  );
}
